// src/pages/UserManagement.js
// Admin-only page to create and manage portal users

import { useState } from "react";
import { createUser, updateUser } from "../firebase/services";
import { Input, Select, Btn, Card, Modal, Tag, Alert, Spinner, PageHeader } from "../components/UI";

const ROLES = [
  { value: "admin",     label: "Admin",      desc: "Full access — add, edit, delete, manage users" },
  { value: "hr",        label: "HR Manager", desc: "Add & edit employees, mark attendance. Cannot delete." },
  { value: "principal", label: "Principal",  desc: "View all records, mark attendance. Cannot edit employees." },
  { value: "viewer",    label: "Viewer",     desc: "View-only access. Cannot make any changes." },
];

const ROLE_COLOR = { admin: "var(--rust)", hr: "var(--slate)", principal: "#2d6a9f", viewer: "#888" };

const PERM_TABLE = [
  { action: "View all employees",     admin: true,  hr: true,  principal: true,  viewer: true  },
  { action: "Add employees",          admin: true,  hr: true,  principal: false, viewer: false },
  { action: "Edit employees",         admin: true,  hr: true,  principal: false, viewer: false },
  { action: "Delete employees",       admin: true,  hr: false, principal: false, viewer: false },
  { action: "Upload documents",       admin: true,  hr: true,  principal: false, viewer: false },
  { action: "Mark attendance",        admin: true,  hr: true,  principal: true,  viewer: false },
  { action: "Upload attendance file", admin: true,  hr: true,  principal: true,  viewer: false },
  { action: "Manage users",           admin: true,  hr: false, principal: false, viewer: false },
];

export default function UserManagement({ users, setUsers }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({ name: "", email: "", password: "", role: "hr" });
  const [creating, setCreating] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState("");
  const [showPerms, setShowPerms] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const create = async () => {
    setError("");
    if (!form.name || !form.email || !form.password) { setError("All fields are required."); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setCreating(true);
    try {
      const u = await createUser(form);
      setUsers(prev => [...prev, { id: u.uid, ...form }]);
      setShowAdd(false);
      setForm({ name: "", email: "", password: "", role: "hr" });
      setSuccess(`✅ User "${form.name}" created successfully.`);
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      const msg = {
        "auth/email-already-in-use": "This email is already registered.",
        "auth/invalid-email": "Invalid email address.",
        "auth/weak-password": "Password is too weak.",
      }[e.code] || e.message;
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (user) => {
    await updateUser(user.id, { active: !user.active });
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, active: !u.active } : u));
  };

  const changeRole = async (user, role) => {
    await updateUser(user.id, { role });
    setUsers(prev => prev.map(u => u.id === user.id ? { ...u, role } : u));
  };

  return (
    <div style={{ animation: "fadeUp .35s ease" }}>
      <PageHeader
        title="Manage Users"
        sub="Create and manage portal accounts with role-based access"
        action={
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="ghost" size="sm" onClick={() => setShowPerms(true)}>📋 Permissions Guide</Btn>
            <Btn onClick={() => setShowAdd(true)}>+ Create User</Btn>
          </div>
        }
      />

      {success && <div style={{ marginBottom: 16 }}><Alert type="success">{success}</Alert></div>}

      {/* Role explanation cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 14, marginBottom: 28 }}>
        {ROLES.map(r => (
          <Card key={r.value} style={{ padding: "14px 16px", borderLeft: `4px solid ${ROLE_COLOR[r.value]}` }}>
            <div style={{ fontWeight: 700, fontSize: 14, fontFamily: "'Syne',sans-serif", marginBottom: 4 }}>{r.label}</div>
            <div style={{ fontSize: 12, color: "var(--slate-light)", lineHeight: 1.5 }}>{r.desc}</div>
          </Card>
        ))}
      </div>

      {/* Users table */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--cream)", fontSize: 11, fontWeight: 700, color: "var(--slate-light)", textTransform: "uppercase", letterSpacing: .6 }}>
                {["Name", "Email", "Role", "Status", "Actions"].map(h => (
                  <th key={h} style={{ padding: "11px 16px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 40, textAlign: "center", color: "var(--slate-light)" }}>No users yet. Create the first user above.</td></tr>
              )}
              {users.map(u => (
                <tr key={u.id} style={{ borderTop: "1px solid #f0ece3" }}>
                  <td style={{ padding: "11px 16px" }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{u.name}</div>
                  </td>
                  <td style={{ padding: "11px 16px", fontSize: 13, color: "var(--slate-light)" }}>{u.email}</td>
                  <td style={{ padding: "11px 16px" }}>
                    <select value={u.role} onChange={e => changeRole(u, e.target.value)}
                      style={{ padding: "5px 10px", border: "1.5px solid #d4d0c8", borderRadius: 7, fontSize: 13, background: "#fff", fontFamily: "'DM Sans',sans-serif" }}>
                      {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "11px 16px" }}>
                    <Tag label={u.active !== false ? "Active" : "Suspended"} color={u.active !== false ? "var(--success)" : "var(--danger)"} />
                  </td>
                  <td style={{ padding: "11px 16px" }}>
                    <Btn size="xs" variant={u.active !== false ? "warn" : "success"} onClick={() => toggleActive(u)}>
                      {u.active !== false ? "Suspend" : "Activate"}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Create user modal */}
      <Modal open={showAdd} onClose={() => { setShowAdd(false); setError(""); }} title="Create New User" width={460}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {error && <Alert type="error">{error}</Alert>}
          <Input label="Full Name" value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Rajan Mishra" />
          <Input label="Email Address" type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="rajan@rka.edu" />
          <Input label="Initial Password" type="password" value={form.password} onChange={e => set("password", e.target.value)} placeholder="Min. 6 characters" />
          <Select label="Role" value={form.role} onChange={e => set("role", e.target.value)}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
          </Select>
          <div style={{ background: "var(--cream)", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "var(--slate-light)", lineHeight: 1.6 }}>
            💡 The user will sign in at your portal URL with this email and password. They can change their password later.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)} disabled={creating}>Cancel</Btn>
            <Btn onClick={create} disabled={creating}>
              {creating ? <><Spinner size={13} color="#fff" /> Creating…</> : "Create User"}
            </Btn>
          </div>
        </div>
      </Modal>

      {/* Permissions reference modal */}
      <Modal open={showPerms} onClose={() => setShowPerms(false)} title="Role Permissions Reference" width={600}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--cream)" }}>
                <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "var(--slate-light)" }}>Permission</th>
                {ROLES.map(r => (
                  <th key={r.value} style={{ padding: "10px 14px", textAlign: "center" }}>
                    <Tag label={r.label} color={ROLE_COLOR[r.value]} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {PERM_TABLE.map(row => (
                <tr key={row.action} style={{ borderTop: "1px solid #f0ece3" }}>
                  <td style={{ padding: "9px 14px", fontSize: 13 }}>{row.action}</td>
                  {["admin", "hr", "principal", "viewer"].map(role => (
                    <td key={role} style={{ padding: "9px 14px", textAlign: "center" }}>
                      {row[role] ? "✅" : "❌"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal>
    </div>
  );
}
